

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiShoppingCart, 
  FiMessageCircle,
  FiChevronLeft,
  FiChevronRight,
  FiTruck,
  FiPackage,
  FiTag,
  FiInfo,
  FiMessageSquare,
  FiLock,
  FiShield,
  FiLogIn,
  FiStar,
  FiHome,
  FiShoppingBag
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { cartAPI } from '../services/api';
import Navbar from '../components/Navbar';
import ProductMetaTags from '../components/ProductMetaTags';
import { productAPI } from '../services/api';
import { supplierAPI } from '../services/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://stelonne-market.online/backend/public';
const SERVICE_CLIENT = "680219057"; // Numéro WhatsApp du service client

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, hasPermission, isAuthenticated } = useAuth();

  // États pour chaque type de données
  const [basicInfo, setBasicInfo] = useState(null);
  const [images, setImages] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  
  // États de chargement individuels
  const [loadingBasic, setLoadingBasic] = useState(true);
  const [loadingImages, setLoadingImages] = useState(true);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [error, setError] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);

  // Nouvelles couleurs de la charte graphique
  const primaryColor = 'text-yellow-400';
  const primaryBg = 'bg-yellow-400';
  const primaryBgHover = 'hover:bg-yellow-500';
  const primaryLight = 'bg-yellow-50';
  const primaryBorder = 'border-yellow-400';
  
  const secondaryColor = 'text-gray-400';
  const secondaryBg = 'bg-gray-400';
  const secondaryBgHover = 'hover:bg-gray-500';
  const secondaryLight = 'bg-gray-100';
  const secondaryBorder = 'border-gray-400';

  useEffect(() => {
    return () => {
      setTimeout(() => {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('resize'));
        }
      }, 100);
    };
  }, []);

  const canViewSupplierDetails = () => {
    
    if (!isAuthenticated || user?.role?.name === 'user' || user?.role === 'user' || user?.role?.name === 'operationnel') {
      return false;
    }
    return true;
  };

  const canContactSuppliers = () => {
    return canViewSupplierDetails();
  };

  const getUserAccessLevel = () => {
    if (isAdmin) return 'Administrateur';
    if (user && user.role) {
      const roleName = typeof user.role === 'object' ? user.role.name : user.role;
      if (roleName === 'Operationnel') return 'Operationnel';
      if (roleName === 'sous_admin') return 'Vendeur';
      if (roleName === 'user') return 'Client';
    }
    if (user) return 'Utilisateur';
    return 'Visiteur';
  };

  // useEffect(() => {
  //   fetchProduct();
  // }, [id]);

  useEffect(() => {
    // Lancer les 3 requêtes en parallèle
    fetchAllData();
  }, [id]);

  const fetchAllData = async () => {
    // Réinitialiser les états
    setError(null);
    
    // Lancer toutes les requêtes en parallèle
    Promise.allSettled([
      fetchBasicInfo(),
      fetchImages(),
      fetchSuppliers()
    ]);
  };

  const fetchBasicInfo = async () => {
    try {
      setLoadingBasic(true);
      console.log(`Chargement des informations de base pour le produit ID: ${id}`);
      const response = await productAPI.getBasicInfo(id);
      console.log('Réponse API informations de base:', response);

      if (response && response.success) {
        setBasicInfo(response.data);
      } else {
        setError('Impossible de charger les informations du produit');
      }
    } catch (error) {
      console.error('Erreur chargement infos de base:', error);
      setError('Erreur de chargement');
    } finally {
      setLoadingBasic(false);
    }
  };

  const fetchImages = async () => {
    try {
      setLoadingImages(true);
      console.log(`Chargement des images pour le produit ID: ${id}`);
      const response = await productAPI.getProductImages(id);
      console.log('Réponse API images:', response);
      
      if (response && response.success) {
        setImages(response.data || []);
        
        // Sélectionner l'image primaire ou la première
        const primaryIndex = response.data.findIndex(img => img.is_primary);
        if (primaryIndex !== -1) {
          setSelectedImageIndex(primaryIndex);
        }
      }
    } catch (error) {
      console.error('Erreur chargement images:', error);
      setImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      console.log(`Chargement des fournisseurs pour le produit ID: ${id}`);
      const response = await supplierAPI.getProductSuppliers(id);
      console.log('Réponse API fournisseurs:', response);
      if (response && response.success) {
        setSuppliers(response.data || []);
      }
    } catch (error) {
      console.error('Erreur chargement fournisseurs:', error);
      setSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const getImageUrl = (imageName) => {
    if (!imageName) return null;
    
    if (imageName.startsWith('http') || imageName.startsWith('blob:')) {
      return imageName;
    }
    
    return `${API_BASE_URL}/storage/products/${imageName}`;
  };

  const getAllImageUrls = (images) => {
    if (!images || !Array.isArray(images)) return [];
    
    return images.map(img => ({
      url: getImageUrl(img.image),
      alt: img.alt_text || `Image du produit`
    }));
  };

  const getPrimaryImage = (images) => {
    if (!images || images.length === 0) return null;
    
    const primary = images.find(img => img.is_primary);
    if (primary) return getImageUrl(primary.image);
    
    return getImageUrl(images[0].image);
  };

  const formatPrice = (price) => {
    const num = parseFloat(price || 0);
    if (isNaN(num)) return '0';

    return num.toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const fetchProduct = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await productAPI.getById(id);
      
      if (response && response.success) {
        if (response.data) {
          const productData = response.data;
          
          const transformedProduct = {
            ...productData,
            images: Array.isArray(productData.images) ? productData.images : [],
            suppliers: productData.suppliers || productData.supplier || [],
            quantity: productData.quantity || 0,
            category: productData.category || { name: 'Non catégorisé' }
          };
          
          setProduct(transformedProduct);
        }
      } else {
        setError('Produit non trouvé');
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du produit:', error);
      setError('Erreur de chargement du produit');
    } finally {
      setLoading(false);
    }
  };

  const addToLocalCart = (item) => {
    try {
      const currentCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
      const existingIndex = currentCart.findIndex(cartItem => cartItem.product_id === item.product_id);
      
      if (existingIndex >= 0) {
        currentCart[existingIndex].quantity += item.quantity;
        if (currentCart[existingIndex].quantity > currentCart[existingIndex].max_quantity) {
          currentCart[existingIndex].quantity = currentCart[existingIndex].max_quantity;
          alert(`Quantité maximale atteinte. Stock: ${currentCart[existingIndex].max_quantity}`);
        }
      } else {
        currentCart.push(item);
      }
      
      localStorage.setItem('guest_cart', JSON.stringify(currentCart));
      updateCartBadge(currentCart.reduce((total, item) => total + item.quantity, 0));
      
      return { success: true, message: 'Produit ajouté au panier local' };
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier local:', error);
      return { success: false, message: 'Erreur lors de l\'ajout au panier' };
    }
  };

  const addToBackendCart = async (item) => {
    try {
      setAddingToCart(true);
      
      if (!user || !user.id) {
        return { 
          success: false, 
          message: 'Utilisateur non identifié. Veuillez vous reconnecter.' 
        };
      }
      
      const cartData = {
        user_id: user.id,
        product_id: item.product_id,
        quantity: item.quantity,
      };
      
      const response = await cartAPI.addToCart(cartData);
      
      if (response.success) {
        if (response.data && response.data.total_items) {
          updateCartBadge(response.data.total_items);
        }
        
        return { 
          success: true, 
          message: response.message || 'Produit ajouté au panier',
          data: response.data
        };
      } else {
        return { 
          success: false, 
          message: response.message || 'Erreur lors de l\'ajout au panier' 
        };
      }
    } catch (error) {
      console.error('Erreur API panier:', error);
      
      if (error.response) {
        switch (error.response.status) {
          case 401:
            return { 
              success: false, 
              message: 'Session expirée. Veuillez vous reconnecter.' 
            };
          case 422:
            return { 
              success: false, 
              message: error.response.data.message || 'Données invalides' 
            };
          default:
            return { 
              success: false, 
              message: 'Erreur de connexion au serveur' 
            };
        }
      }
      
      return { 
        success: false, 
        message: 'Erreur de connexion au serveur' 
      };
    } finally {
      setAddingToCart(false);
    }
  };

  const updateCartBadge = (itemCount) => {
    const badgeElement = document.querySelector('.cart-badge');
    if (badgeElement) {
      badgeElement.textContent = itemCount > 99 ? '99+' : itemCount;
      badgeElement.classList.toggle('hidden', itemCount === 0);
    }
  };

  const handleAddToCart = async () => {
    if (!basicInfo) return;
    
    const stockQuantity = basicInfo.quantity || 0;
    if (stockQuantity === 0) {
      alert('❌ Ce produit est en rupture de stock !');
      return;
    }
    
    const cartItem = {
      // product_id: product.id,
      // name: product.name,
      // price: product.price,
      // image: getPrimaryImage(product.images),
      // quantity: 1,
      // max_quantity: stockQuantity,

      product_id: basicInfo.id,
      name: basicInfo.name,
      price: basicInfo.price,
      image: getPrimaryImage(),
      quantity: 1,
      max_quantity: stockQuantity,
    };
    
    if (isAuthenticated && user) {
      const result = await addToBackendCart(cartItem);
      
      if (result.success) {
        console.log(`✅ ${basicInfo.name} ajouté au panier !`);
      } else {
        alert(`❌ ${result.message}`);
        
        if (result.message.includes('Session expirée') || result.message.includes('reconnecter')) {
          setTimeout(() => {
            navigate('/login', { 
              state: { 
                from: window.location.pathname,
                message: 'Session expirée. Veuillez vous reconnecter.'
              } 
            });
          }, 1500);
        }
      }
    } else {
      const result = addToLocalCart(cartItem);
      
      if (result.success) {
        console.log('✅ Produit ajouté au panier local !');
      } else {
        alert(`❌ ${result.message}`);
      }
    }
  };

  const handleWhatsAppClick = () => {
    if (!basicInfo) return;

    const primaryImage = getPrimaryImage(images.images);
    const productImageUrl = primaryImage || 'Image non disponible';
    
    const message = `Bonjour StelonneMarket,

Je suis intéressé(e) par le produit suivant :

📦 **Produit :** ${basicInfo.name}
📝 **Description :** ${basicInfo.description || 'Pas de description'}
💰 **Prix :** ${formatPrice(basicInfo.price)} FCFA

${primaryImage ? `🖼️ Voir l'image : ${productImageUrl}` : ''}

information sur le produit : https://stelonne-market.online/product/${id}

Pouvez-vous me donner plus d'informations sur ce produit ?
Est-il disponible immédiatement ?

Merci !

Cordialement`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${SERVICE_CLIENT}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handleContactSupplier = (supplier, product, images) => {
    if (!canContactSuppliers()) {
      alert('Vous n\'avez pas l\'autorisation de contacter les fournisseurs.');
      return;
    }

    if (!supplier.phone) {
      alert(`Aucun numéro WhatsApp disponible pour ${images.name}`);
      return;
    }

    const primaryImage = getPrimaryImage(images.images);
    const productImageUrl = primaryImage || 'Image non disponible';
    
    const message = `Bonjour ${supplier.name},

Je suis intéressé(e) par le produit suivant :

📦 **Produit :** ${product.name}
📍 **Votre ville :** ${supplier.city || 'Non spécifiée'}

${primaryImage ? `🖼️ Voir l'image du produit : ${productImageUrl}` : ''}

Pouvez-vous me confirmer si vous avez ce produit en stock ?
Si oui, quel est votre meilleur prix ?

Merci de votre retour !

Cordialement`;

    const encodedMessage = encodeURIComponent(message);
    const cleanPhone = supplier.phone.replace(/\D/g, '');
    let whatsappNumber = cleanPhone;
    
    if (!cleanPhone.startsWith('237') && cleanPhone.length === 9) {
      whatsappNumber = `237${cleanPhone}`;
    }
    
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handlePreviousImage = () => {
    if (basicInfo && images && images.length > 0) {
      setSelectedImageIndex(prev => 
        prev === 0 ? images.length - 1 : prev - 1
      );
    }
  };

  const handleNextImage = () => {
    if (basicInfo && images && images.length > 0) {
      setSelectedImageIndex(prev => 
        prev === images.length - 1 ? 0 : prev + 1
      );
    }
  };

  // Récupérer l'URL précédente ou '/' par défaut
  const previousPage = location.state?.from || '/';

  const handleBack = () => {
    navigate(previousPage);
  };

  const getCartButtonText = () => {
    if (addingToCart) return 'Ajout en cours...';
    if (!basicInfo || basicInfo.quantity === 0) return 'Rupture';
    if (!isAuthenticated) return 'Ajouter (local)';
    return 'Ajouter au panier';
  };

  const getCartButtonClass = () => {
    if (!basicInfo || basicInfo.quantity === 0) {
      return 'bg-gray-200 text-gray-500 cursor-not-allowed border border-gray-300';
    }
    if (addingToCart) {
      return 'bg-yellow-400 text-white cursor-wait border border-yellow-500';
    }
    if (!isAuthenticated) {
      return `${secondaryLight} text-gray-700 hover:${secondaryBgHover} border ${secondaryBorder}`;
    }
    return `${primaryBg} text-white ${primaryBgHover} border ${primaryBorder}`;
  };

  // if ((loadingBasic && !basicInfo) || (loadingImages && images.length === 0) || (loadingSuppliers && suppliers.length === 0)) {
  //   return (
  //     <div className="min-h-screen bg-gray-50">
  //       <Navbar />
  //       <div className="flex flex-col justify-center items-center h-64">
  //         <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-yellow-400 mb-4"></div>
  //         <p className="text-gray-600">Chargement du produit...</p>
  //       </div>
  //     </div>
  //   );
  // }

  if (error) { // || !basicInfo) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiShoppingBag className="text-3xl text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">{error || 'Produit non trouvé'}</h2>
          <p className="text-gray-600 mb-6">Le produit que vous recherchez n'existe pas ou a été supprimé.</p>
          <button 
            onClick={handleBack}
            className={`px-6 py-3 ${primaryBg} text-white rounded-xl font-semibold ${primaryBgHover} shadow-md hover:shadow-lg transition-all`}
          >
            <FiHome className="inline mr-2" />
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  const imageUrls = getAllImageUrls(images);
  const primaryImageUrl = imageUrls[selectedImageIndex]?.url || getPrimaryImage(images) || '/placeholder.jpg';
  const userCanViewSuppliers = canViewSupplierDetails();
  const userCanContactSuppliers = canContactSuppliers();

  return (
    <>
      {/* Balises SEO dynamiques pour CE produit */}
      {basicInfo && (
        <ProductMetaTags product={{
          ...basicInfo,
          images: images,
          suppliers: suppliers
        }} />
      )}
      
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <Navbar />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Bouton retour amélioré */}
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8 group transition-all duration-200"
          >
            <div className={`p-2 ${secondaryLight} rounded-lg group-hover:${secondaryBgHover}`}>
              <FiArrowLeft className="text-lg group-hover:-translate-x-1 transition-transform" />
            </div>
            <span className="font-semibold group-hover:text-gray-900">Retour aux produits</span>
          </button>

          {/* Contenu principal */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 sm:p-8">
              {/* Section images */}
              <div className="space-y-4">
                {/* Image principale avec overlay */}
                <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl overflow-hidden aspect-square group">
                  {loadingImages ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="animate-pulse text-gray-400">Chargement des images...</div>
                    </div>
                  ) : (
                    <img
                      src={primaryImageUrl}
                      alt={basicInfo?.name}
                      className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = '/placeholder.jpg';
                      }}
                    />
                  )}
                  
                  {/* Navigation entre images */}
                  {imageUrls.length > 1 && (
                    <>
                      <button
                        onClick={handlePreviousImage}
                        className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${secondaryLight} hover:bg-white p-3 rounded-full shadow-lg transition-all duration-200`}
                      >
                        <FiChevronLeft className="text-xl text-gray-600" />
                      </button>
                      <button
                        onClick={handleNextImage}
                        className={`absolute right-4 top-1/2 transform -translate-y-1/2 ${secondaryLight} hover:bg-white p-3 rounded-full shadow-lg transition-all duration-200`}
                      >
                        <FiChevronRight className="text-xl text-gray-600" />
                      </button>
                      
                      {/* Indicateur d'image */}
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                        {imageUrls.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setSelectedImageIndex(index)}
                            className={`w-2 h-2 rounded-full transition-all ${
                              index === selectedImageIndex 
                                ? `${primaryBg} w-4` 
                                : `${secondaryBg} hover:${secondaryBgHover}`
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
                
                {/* Galerie de miniatures */}
                {imageUrls.length > 1 && (
                  <div className="flex gap-3 overflow-x-auto py-2 scrollbar-thin scrollbar-thumb-gray-300">
                    {imageUrls.map((img, index) => (
                      <button
                        key={img.id || index}
                        onClick={() => setSelectedImageIndex(index)}
                        className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                          index === selectedImageIndex 
                            ? `${primaryBorder} ring-2 ring-yellow-200` 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {loadingImages ? (
                          <div className="w-full h-full bg-gray-200 animate-pulse"></div>
                        ) : (
                          <img
                            src={img.url}
                            alt={img.alt}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Détails produit */}
              <div className="space-y-6">
                {/* Titre et description */}
                <div>
                  <div className="inline-flex items-center gap-2 mb-3">
                    {basicInfo?.category_name && (
                      <span className={`px-3 py-1 ${primaryLight} ${primaryColor} rounded-full text-xs font-semibold`}>
                        {basicInfo.category_name}
                      </span>
                    )}
                  </div>
                  
                  <h1 className="text-3xl font-bold text-gray-900 mb-4">
                    {basicInfo?.name}
                  </h1>
                  
                  {basicInfo?.description && (
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 ${primaryLight} rounded-lg flex-shrink-0`}>
                          <FiInfo className={`${primaryColor}`} />
                        </div>
                        <p className="text-gray-600 leading-relaxed">
                          {basicInfo.description}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Informations complémentaires */}
                <div className="space-y-4 border-t border-b border-gray-200 py-6">
                  {/* Catégorie */}
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <FiTag className="text-blue-500" />
                    </div>
                    <div>
                      <span className="text-sm text-gray-500 block">Catégorie</span>
                      <span className="font-semibold text-gray-800">
                        {basicInfo?.category_name || 'Non catégorisé'}
                      </span>
                    </div>
                  </div>

                  {/* Stock */}
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-50 rounded-lg">
                      <FiPackage className="text-purple-500" />
                    </div>
                    <div>
                      <span className="text-sm text-gray-500 block">Disponibilité</span>
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                        basicInfo?.quantity > 10 ? 'bg-green-100 text-green-800' : 
                        basicInfo?.quantity > 0 ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {basicInfo?.quantity > 0 
                          ? `unité${basicInfo.quantity > 1 ? 's' : ''} en stock` 
                          : 'Rupture de stock'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Prix et actions */}
                <div className="space-y-6">
                  {/* Prix */}
                  <div>
                    <span className="text-sm text-gray-500 block mb-1">Prix</span>
                    <div className="flex items-baseline gap-3">
                      <span className="text-4xl font-bold text-gray-900">
                        {formatPrice(basicInfo?.price)} FCFA
                      </span>
                      
                      {basicInfo?.discount_price && (
                        <div className="flex items-center gap-2">
                          <span className="text-2xl text-gray-400 line-through">
                            {formatPrice(basicInfo.discount_price)} FCFA
                          </span>
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-sm font-bold rounded-lg border border-yellow-200">
                            -{Math.round(((basicInfo.price - basicInfo.discount_price) / basicInfo.price) * 100)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Boutons d'action */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleAddToCart}
                      disabled={basicInfo?.quantity === 0 || addingToCart}
                      className={`flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-bold transition-all ${getCartButtonClass()}`}
                      title={
                        basicInfo?.quantity === 0 ? "Produit en rupture de stock" :
                        !isAuthenticated ? "Ajouter au panier local (non connecté)" :
                        "Ajouter au panier"
                      }
                    >
                      <FiShoppingCart className="text-xl" />
                      {!isAuthenticated && <FiLogIn className="text-xs" />}
                      <span className="text-lg">
                        {getCartButtonText()}
                      </span>
                    </button>

                    <button
                      onClick={handleWhatsAppClick}
                      className="flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-gray-500 to-gray-500 text-white rounded-xl font-bold hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
                    >
                      <FiMessageCircle className="text-xl" />
                      <span className="text-lg">Discuter sur WhatsApp</span>
                    </button>
                  </div>

                  {/* Informations supplémentaires */}
                  <div className="pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>Livraison disponible</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span>Support 7j/7</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                        <span>Paiement sécurisé</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION DES FOURNISSEURS - SEULEMENT POUR LES UTILISATEURS AUTORISÉS */}
            {suppliers && suppliers.length > 0 && userCanViewSuppliers && (
              <div className="p-6 sm:p-8 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 ${primaryLight} rounded-lg`}>
                      <FiTruck className={`text-xl ${primaryColor}`} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        Informations fournisseurs
                      </h3>
                      <p className="text-sm text-gray-600">
                        Accès: {getUserAccessLevel()} | {loadingSuppliers ? 'Chargement...' : `${suppliers.length} fournisseur(s)`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-800 rounded-full border border-yellow-200">
                    <FiShield className="text-sm" />
                    <span className="text-sm font-semibold">Accès autorisé</span>
                  </div>
                </div>
                
                <p className="text-gray-600 mb-8 max-w-3xl">
                  Ce produit est disponible chez {suppliers.length} fournisseur{suppliers.length > 1 ? 's' : ''}. 
                  {userCanContactSuppliers ? ' Vous pouvez contacter chacun d\'eux directement via WhatsApp.' : ''}
                </p>
                
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-700">
                    Liste des fournisseurs ({suppliers.length})
                  </h4>
                  
                  {loadingSuppliers ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {[1, 2, 3, 4].map(i => (
                        <div key={i} className="bg-white rounded-xl shadow-md border border-gray-200 p-4 animate-pulse">
                          <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
                          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                          <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                          <div className="h-8 bg-gray-200 rounded"></div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {suppliers.map((supplier, index) => (
                        <div 
                          key={supplier.id || index}
                          className={`bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border overflow-hidden group ${
                            userCanContactSuppliers ? 'cursor-pointer border-gray-200' : 'cursor-not-allowed border-gray-200'
                          }`}
                          onClick={() => userCanContactSuppliers && handleContactSupplier(supplier, basicInfo, images)}
                        >
                          <div className="p-4">
                            {/* En-tête avec nom et ville */}
                            <div className="mb-3">
                              <h5 className="font-bold text-gray-800 group-hover:text-gray-900 transition-colors">
                                {supplier.name}
                              </h5>
                              {supplier.city && (
                                <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                  </svg>
                                  <span>{supplier.city}</span>
                                </div>
                              )}
                            </div>

                            {/* Informations détaillées */}
                            <div className="text-xs text-gray-500 space-y-1 mb-4">
                              {supplier.email && (
                                <div className="truncate">📧 {supplier.email}</div>
                              )}
                              {supplier.address && (
                                <div className="truncate">📍 {supplier.address}</div>
                              )}
                              {supplier.phone && userCanContactSuppliers && (
                                <div className="truncate">📞 {supplier.phone}</div>
                              )}
                            </div>

                            {/* Bouton WhatsApp */}
                            {userCanContactSuppliers && (
                              <div className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
                                supplier.phone 
                                  ? 'bg-gray-100 text-gray-700 group-hover:bg-gray-800 group-hover:text-white' 
                                  : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                              }`}>
                                <FiMessageSquare className="text-lg" />
                                <span className="font-semibold">
                                  {!supplier.phone ? 'Sans contact' : 'Contacter sur WhatsApp'}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* Indicateur de clic */}
                          {userCanContactSuppliers && (
                            <div className={`text-xs font-medium px-3 py-1 text-center ${
                              supplier.phone 
                                ? 'bg-gray-100 text-gray-700' 
                                : 'bg-gray-200 text-gray-500'
                            }`}>
                              {supplier.phone 
                                ? 'Cliquez pour contacter sur WhatsApp' 
                                : 'Contact non disponible'}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Section informations supplémentaires (visible pour tous) */}
            <div className="p-6 sm:p-8 border-t border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <FiStar className="text-yellow-400 text-xl" />
                <h3 className="text-xl font-bold text-gray-900">Informations complémentaires</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-700">Livraison</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>Suivi de commande disponible</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>Livraison dans tout le pays</span>
                    </li>
                  </ul>
                </div>
                
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-700">Garantie & Support</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span>Support client 7j/7 via WhatsApp</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span>Service après-vente disponible</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductDetail;

// const ProductDetail = () => {
//   const { id } = useParams();
//   const navigate = useNavigate();
//   const { user, isAdmin, hasPermission, isAuthenticated } = useAuth();

//   // États pour chaque type de données
//   const [basicInfo, setBasicInfo] = useState(null);
//   const [images, setImages] = useState([]);
//   const [suppliers, setSuppliers] = useState([]);
  
//   // États de chargement individuels
//   const [loadingBasic, setLoadingBasic] = useState(true);
//   const [loadingImages, setLoadingImages] = useState(true);
//   const [loadingSuppliers, setLoadingSuppliers] = useState(true);


//   const [product, setProduct] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [selectedImageIndex, setSelectedImageIndex] = useState(0);
//   const [error, setError] = useState(null);
//   const [addingToCart, setAddingToCart] = useState(false);

//   // Nouvelles couleurs de la charte graphique
//   const primaryColor = 'text-yellow-400';
//   const primaryBg = 'bg-yellow-400';
//   const primaryBgHover = 'hover:bg-yellow-500';
//   const primaryLight = 'bg-yellow-50';
//   const primaryBorder = 'border-yellow-400';
  
//   const secondaryColor = 'text-gray-400';
//   const secondaryBg = 'bg-gray-400';
//   const secondaryBgHover = 'hover:bg-gray-500';
//   const secondaryLight = 'bg-gray-100';
//   const secondaryBorder = 'border-gray-400';

//   useEffect(() => {
//     return () => {
//       setTimeout(() => {
//         if (typeof window !== 'undefined') {
//           window.dispatchEvent(new Event('resize'));
//         }
//       }, 100);
//     };
//   }, []);

//   const canViewSupplierDetails = () => {
//     if (!isAuthenticated || user?.role?.name === 'user' || user?.role === 'user' || user?.role?.name === 'operationnel') {
//       return false;
//     }
//     return true;
//   };

//   const canContactSuppliers = () => {
//     return canViewSupplierDetails();
//   };

//   const getUserAccessLevel = () => {
//     if (isAdmin) return 'Administrateur';
//     if (user && user.role) {
//       const roleName = typeof user.role === 'object' ? user.role.name : user.role;
//       if (roleName === 'Operationnel') return 'Operationnel';
//       if (roleName === 'sous_admin') return 'Vendeur';
//       if (roleName === 'user') return 'Client';
//     }
//     if (user) return 'Utilisateur';
//     return 'Visiteur';
//   };

//   // useEffect(() => {
//   //   fetchProduct();
//   // }, [id]);

//   useEffect(() => {
//     // Lancer les 3 requêtes en parallèle
//     fetchAllData();
//   }, [id]);

//   const fetchAllData = async () => {
//     // Réinitialiser les états
//     setError(null);
    
//     // Lancer toutes les requêtes en parallèle
//     Promise.allSettled([
//       fetchBasicInfo(),
//       fetchImages(),
//       fetchSuppliers()
//     ]);
//   };

//   const fetchBasicInfo = async () => {
//     try {
//       setLoadingBasic(true);
      
//       const response = await productAPI.getBasicInfo(id);

//       if (response && response.success) {
//         setBasicInfo(response.data);
//       } else {
//         setError('Impossible de charger les informations du produit');
//       }
//     } catch (error) {
//       console.error('Erreur chargement infos de base:', error);
//       setError('Erreur de chargement');
//     } finally {
//       setLoadingBasic(false);
//     }
//   };

//   const fetchImages = async () => {
//     try {
//       setLoadingImages(true);
//       const response = await productAPI.getProductImages(id);
      
//       if (response && response.success) {
//         setImages(response.data || []);
        
//         // Sélectionner l'image primaire ou la première
//         const primaryIndex = response.data.findIndex(img => img.is_primary);
//         if (primaryIndex !== -1) {
//           setSelectedImageIndex(primaryIndex);
//         }
//       }
//     } catch (error) {
//       console.error('Erreur chargement images:', error);
//       setImages([]);
//     } finally {
//       setLoadingImages(false);
//     }
//   };

//   const fetchSuppliers = async () => {
//     try {
//       setLoadingSuppliers(true);
//       const response = await supplierAPI.getProductSuppliers(id);
//       if (response && response.success) {
//         setSuppliers(response.data || []);
//       }
//     } catch (error) {
//       console.error('Erreur chargement fournisseurs:', error);
//       setSuppliers([]);
//     } finally {
//       setLoadingSuppliers(false);
//     }
//   };

//   const getImageUrl = (imageName) => {
//     if (!imageName) return null;
    
//     if (imageName.startsWith('http') || imageName.startsWith('blob:')) {
//       return imageName;
//     }
    
//     return `${API_BASE_URL}/storage/products/${imageName}`;
//   };

//   const getAllImageUrls = (images) => {
//     if (!images || !Array.isArray(images)) return [];
    
//     return images.map(img => ({
//       url: getImageUrl(img.image),
//       alt: img.alt_text || `Image du produit`
//     }));
//   };

//   const getPrimaryImage = (images) => {
//     if (!images || images.length === 0) return null;
    
//     const primary = images.find(img => img.is_primary);
//     if (primary) return getImageUrl(primary.image);
    
//     return getImageUrl(images[0].image);
//   };

//   const formatPrice = (price) => {
//     const num = parseFloat(price || 0);
//     if (isNaN(num)) return '0';

//     return num.toLocaleString('fr-FR', {
//       minimumFractionDigits: 0,
//       maximumFractionDigits: 0
//     });
//   };

//   const fetchProduct = async () => {
//     try {
//       setLoading(true);
//       setError(null);
      
//       const response = await productAPI.getById(id);
      
//       if (response && response.success) {
//         if (response.data) {
//           const productData = response.data;
          
//           const transformedProduct = {
//             ...productData,
//             images: Array.isArray(productData.images) ? productData.images : [],
//             suppliers: productData.suppliers || productData.supplier || [],
//             quantity: productData.quantity || 0,
//             category: productData.category || { name: 'Non catégorisé' }
//           };
          
//           setProduct(transformedProduct);
//         }
//       } else {
//         setError('Produit non trouvé');
//       }
//     } catch (error) {
//       console.error('Erreur lors de la récupération du produit:', error);
//       setError('Erreur de chargement du produit');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const addToLocalCart = (item) => {
//     try {
//       const currentCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
//       const existingIndex = currentCart.findIndex(cartItem => cartItem.product_id === item.product_id);
      
//       if (existingIndex >= 0) {
//         currentCart[existingIndex].quantity += item.quantity;
//         if (currentCart[existingIndex].quantity > currentCart[existingIndex].max_quantity) {
//           currentCart[existingIndex].quantity = currentCart[existingIndex].max_quantity;
//           alert(`Quantité maximale atteinte. Stock: ${currentCart[existingIndex].max_quantity}`);
//         }
//       } else {
//         currentCart.push(item);
//       }
      
//       localStorage.setItem('guest_cart', JSON.stringify(currentCart));
//       updateCartBadge(currentCart.reduce((total, item) => total + item.quantity, 0));
      
//       return { success: true, message: 'Produit ajouté au panier local' };
//     } catch (error) {
//       console.error('Erreur lors de l\'ajout au panier local:', error);
//       return { success: false, message: 'Erreur lors de l\'ajout au panier' };
//     }
//   };

//   const addToBackendCart = async (item) => {
//     try {
//       setAddingToCart(true);
      
//       if (!user || !user.id) {
//         return { 
//           success: false, 
//           message: 'Utilisateur non identifié. Veuillez vous reconnecter.' 
//         };
//       }
      
//       const cartData = {
//         user_id: user.id,
//         product_id: item.product_id,
//         quantity: item.quantity,
//       };
      
//       const response = await cartAPI.addToCart(cartData);
      
//       if (response.success) {
//         if (response.data && response.data.total_items) {
//           updateCartBadge(response.data.total_items);
//         }
        
//         return { 
//           success: true, 
//           message: response.message || 'Produit ajouté au panier',
//           data: response.data
//         };
//       } else {
//         return { 
//           success: false, 
//           message: response.message || 'Erreur lors de l\'ajout au panier' 
//         };
//       }
//     } catch (error) {
//       console.error('Erreur API panier:', error);
      
//       if (error.response) {
//         switch (error.response.status) {
//           case 401:
//             return { 
//               success: false, 
//               message: 'Session expirée. Veuillez vous reconnecter.' 
//             };
//           case 422:
//             return { 
//               success: false, 
//               message: error.response.data.message || 'Données invalides' 
//             };
//           default:
//             return { 
//               success: false, 
//               message: 'Erreur de connexion au serveur' 
//             };
//         }
//       }
      
//       return { 
//         success: false, 
//         message: 'Erreur de connexion au serveur' 
//       };
//     } finally {
//       setAddingToCart(false);
//     }
//   };

//   const updateCartBadge = (itemCount) => {
//     const badgeElement = document.querySelector('.cart-badge');
//     if (badgeElement) {
//       badgeElement.textContent = itemCount > 99 ? '99+' : itemCount;
//       badgeElement.classList.toggle('hidden', itemCount === 0);
//     }
//   };

//   const handleAddToCart = async () => {
//     if (!basicInfo) return;
    
//     const stockQuantity = basicInfo.quantity || 0;
//     if (stockQuantity === 0) {
//       alert('❌ Ce produit est en rupture de stock !');
//       return;
//     }
    
//     const cartItem = {
//       // product_id: product.id,
//       // name: product.name,
//       // price: product.price,
//       // image: getPrimaryImage(product.images),
//       // quantity: 1,
//       // max_quantity: stockQuantity,

//       product_id: basicInfo.id,
//       name: basicInfo.name,
//       price: basicInfo.price,
//       image: getPrimaryImage(),
//       quantity: 1,
//       max_quantity: stockQuantity,
//     };
    
//     if (isAuthenticated && user) {
//       const result = await addToBackendCart(cartItem);
      
//       if (result.success) {
//         console.log(`✅ ${basicInfo.name} ajouté au panier !`);
//       } else {
//         alert(`❌ ${result.message}`);
        
//         if (result.message.includes('Session expirée') || result.message.includes('reconnecter')) {
//           setTimeout(() => {
//             navigate('/login', { 
//               state: { 
//                 from: window.location.pathname,
//                 message: 'Session expirée. Veuillez vous reconnecter.'
//               } 
//             });
//           }, 1500);
//         }
//       }
//     } else {
//       const result = addToLocalCart(cartItem);
      
//       if (result.success) {
//         console.log('✅ Produit ajouté au panier local !');
//       } else {
//         alert(`❌ ${result.message}`);
//       }
//     }
//   };

//   const handleWhatsAppClick = () => {
//     if (!basicInfo) return;

//     const primaryImage = getPrimaryImage(images.images);
//     const productImageUrl = primaryImage || 'Image non disponible';
    
//     const message = `Bonjour StelonneMarket,

// Je suis intéressé(e) par le produit suivant :

// 📦 **Produit :** ${basicInfo.name}
// 📝 **Description :** ${basicInfo.description || 'Pas de description'}
// 💰 **Prix :** ${formatPrice(basicInfo.price)} FCFA

// ${primaryImage ? `🖼️ Voir l'image : ${productImageUrl}` : ''}

// produit disponible sur : https://stelonne-market.online/product/${id}

// Pouvez-vous me donner plus d'informations sur ce produit ?
// Est-il disponible immédiatement ?

// Merci !

// Cordialement`;

//     const encodedMessage = encodeURIComponent(message);
//     const whatsappUrl = `https://wa.me/${SERVICE_CLIENT}?text=${encodedMessage}`;
    
//     window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
//   };

//   const handleContactSupplier = (supplier, product, images) => {
//     if (!canContactSuppliers()) {
//       alert('Vous n\'avez pas l\'autorisation de contacter les fournisseurs.');
//       return;
//     }

//     if (!supplier.phone) {
//       alert(`Aucun numéro WhatsApp disponible pour ${images.name}`);
//       return;
//     }

//     const primaryImage = getPrimaryImage(images.images);
//     const productImageUrl = primaryImage || 'Image non disponible';
    
//     const message = `Bonjour ${supplier.name},

// Je suis intéressé(e) par le produit suivant :

// 📦 **Produit :** ${product.name}
// 📍 **Votre ville :** ${supplier.city || 'Non spécifiée'}

// ${primaryImage ? `🖼️ Voir l'image du produit : ${productImageUrl}` : ''}

// Pouvez-vous me confirmer si vous avez ce produit en stock ?
// Si oui, quel est votre meilleur prix ?

// Merci de votre retour !

// Cordialement`;

//     const encodedMessage = encodeURIComponent(message);
//     const cleanPhone = supplier.phone.replace(/\D/g, '');
//     let whatsappNumber = cleanPhone;
    
//     if (!cleanPhone.startsWith('237') && cleanPhone.length === 9) {
//       whatsappNumber = `237${cleanPhone}`;
//     }
    
//     const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
//     window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
//   };

//   const handlePreviousImage = () => {
//     if (basicInfo && images && images.length > 0) {
//       setSelectedImageIndex(prev => 
//         prev === 0 ? images.length - 1 : prev - 1
//       );
//     }
//   };

//   const handleNextImage = () => {
//     if (basicInfo && images && images.length > 0) {
//       setSelectedImageIndex(prev => 
//         prev === images.length - 1 ? 0 : prev + 1
//       );
//     }
//   };

//   const handleBack = () => {
//     navigate('/');
//   };

//   const getCartButtonText = () => {
//     if (addingToCart) return 'Ajout en cours...';
//     if (!basicInfo || basicInfo.quantity === 0) return 'Rupture';
//     if (!isAuthenticated) return 'Ajouter (local)';
//     return 'Ajouter au panier';
//   };

//   const getCartButtonClass = () => {
//     if (!basicInfo || basicInfo.quantity === 0) {
//       return 'bg-gray-200 text-gray-500 cursor-not-allowed border border-gray-300';
//     }
//     if (addingToCart) {
//       return 'bg-yellow-400 text-white cursor-wait border border-yellow-500';
//     }
//     if (!isAuthenticated) {
//       return `${secondaryLight} text-gray-700 hover:${secondaryBgHover} border ${secondaryBorder}`;
//     }
//     return `${primaryBg} text-white ${primaryBgHover} border ${primaryBorder}`;
//   };

//   if (error || !basicInfo) {
//     return (
//       <div className="min-h-screen bg-gray-50">
//         <Navbar />
//         <div className="text-center py-16">
//           <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
//             <FiShoppingBag className="text-3xl text-gray-400" />
//           </div>
//           <h2 className="text-2xl font-bold text-gray-900 mb-3">{error || 'Produit non trouvé'}</h2>
//           <p className="text-gray-600 mb-6">Le produit que vous recherchez n'existe pas ou a été supprimé.</p>
//           <button 
//             onClick={handleBack}
//             className={`px-6 py-3 ${primaryBg} text-white rounded-xl font-semibold ${primaryBgHover} shadow-md hover:shadow-lg transition-all`}
//           >
//             <FiHome className="inline mr-2" />
//             Retour à l'accueil
//           </button>
//         </div>
//       </div>
//     );
//   }

//   const imageUrls = getAllImageUrls(images);
//   const primaryImageUrl = imageUrls[selectedImageIndex]?.url || getPrimaryImage(images) || '/placeholder.jpg';
//   const userCanViewSuppliers = canViewSupplierDetails();
//   const userCanContactSuppliers = canContactSuppliers();

//   return (
//     <>
//       {/* Balises SEO dynamiques pour CE produit */}
//       {basicInfo && (
//         <ProductMetaTags product={{
//           ...basicInfo,
//           images: images,
//           suppliers: suppliers
//         }} />
//       )}

//       <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
//         <Navbar />
        
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
//           {/* Bouton retour amélioré */}
//           <button
//             onClick={handleBack}
//             className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8 group transition-all duration-200"
//           >
//             <div className={`p-2 ${secondaryLight} rounded-lg group-hover:${secondaryBgHover}`}>
//               <FiArrowLeft className="text-lg group-hover:-translate-x-1 transition-transform" />
//             </div>
//             <span className="font-semibold group-hover:text-gray-900">Retour aux produits</span>
//           </button>

//           {/* Contenu principal */}
//           <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
//             <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 sm:p-8">
//               {/* Section images */}
//               <div className="space-y-4">
//                 {/* Image principale avec overlay */}
//                 <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl overflow-hidden aspect-square group">
//                   {loadingImages ? (
//                     <div className="w-full h-full flex items-center justify-center">
//                       <div className="animate-pulse text-gray-400">Chargement des images...</div>
//                     </div>
//                   ) : (
//                     <img
//                       src={primaryImageUrl}
//                       alt={basicInfo?.name}
//                       className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
//                       onError={(e) => {
//                         e.target.src = '/placeholder.jpg';
//                       }}
//                     />
//                   )}
                  
//                   {/* Navigation entre images */}
//                   {imageUrls.length > 1 && (
//                     <>
//                       <button
//                         onClick={handlePreviousImage}
//                         className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${secondaryLight} hover:bg-white p-3 rounded-full shadow-lg transition-all duration-200`}
//                       >
//                         <FiChevronLeft className="text-xl text-gray-600" />
//                       </button>
//                       <button
//                         onClick={handleNextImage}
//                         className={`absolute right-4 top-1/2 transform -translate-y-1/2 ${secondaryLight} hover:bg-white p-3 rounded-full shadow-lg transition-all duration-200`}
//                       >
//                         <FiChevronRight className="text-xl text-gray-600" />
//                       </button>
                      
//                       {/* Indicateur d'image */}
//                       <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
//                         {imageUrls.map((_, index) => (
//                           <button
//                             key={index}
//                             onClick={() => setSelectedImageIndex(index)}
//                             className={`w-2 h-2 rounded-full transition-all ${
//                               index === selectedImageIndex 
//                                 ? `${primaryBg} w-4` 
//                                 : `${secondaryBg} hover:${secondaryBgHover}`
//                             }`}
//                           />
//                         ))}
//                       </div>
//                     </>
//                   )}
//                 </div>
                
//                 {/* Galerie de miniatures */}
//                 {imageUrls.length > 1 && (
//                   <div className="flex gap-3 overflow-x-auto py-2 scrollbar-thin scrollbar-thumb-gray-300">
//                     {imageUrls.map((img, index) => (
//                       <button
//                         key={img.id || index}
//                         onClick={() => setSelectedImageIndex(index)}
//                         className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all duration-200 ${
//                           index === selectedImageIndex 
//                             ? `${primaryBorder} ring-2 ring-yellow-200` 
//                             : 'border-gray-200 hover:border-gray-300'
//                         }`}
//                       >
//                         {loadingImages ? (
//                           <div className="w-full h-full bg-gray-200 animate-pulse"></div>
//                         ) : (
//                           <img
//                             src={img.url}
//                             alt={img.alt}
//                             className="w-full h-full object-cover"
//                           />
//                         )}
//                       </button>
//                     ))}
//                   </div>
//                 )}
//               </div>

//               {/* Détails produit */}
//               <div className="space-y-6">
//                 {/* Titre et description */}
//                 <div>
//                   <div className="inline-flex items-center gap-2 mb-3">
//                     {basicInfo?.category_name && (
//                       <span className={`px-3 py-1 ${primaryLight} ${primaryColor} rounded-full text-xs font-semibold`}>
//                         {basicInfo.category_name}
//                       </span>
//                     )}
//                   </div>
                  
//                   <h1 className="text-3xl font-bold text-gray-900 mb-4">
//                     {basicInfo?.name}
//                   </h1>
                  
//                   {basicInfo?.description && (
//                     <div className="space-y-3">
//                       <div className="flex items-start gap-3">
//                         <div className={`p-2 ${primaryLight} rounded-lg flex-shrink-0`}>
//                           <FiInfo className={`${primaryColor}`} />
//                         </div>
//                         <p className="text-gray-600 leading-relaxed">
//                           {basicInfo?.description}
//                         </p>
//                       </div>
//                     </div>
//                   )}
//                 </div>

//                 {/* Informations complémentaires */}
//                 <div className="space-y-4 border-t border-b border-gray-200 py-6">
//                   {/* Catégorie */}
//                   <div className="flex items-center gap-3">
//                     <div className="p-2 bg-blue-50 rounded-lg">
//                       <FiTag className="text-blue-500" />
//                     </div>
//                     <div>
//                       <span className="text-sm text-gray-500 block">Catégorie</span>
//                       <span className="font-semibold text-gray-800">
//                         {basicInfo?.category_name || 'Non catégorisé'}
//                       </span>
//                     </div>
//                   </div>

//                   {/* Stock */}
//                   <div className="flex items-center gap-3">
//                     <div className="p-2 bg-purple-50 rounded-lg">
//                       <FiPackage className="text-purple-500" />
//                     </div>
//                     <div>
//                       <span className="text-sm text-gray-500 block">Disponibilité</span>
//                       <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
//                         basicInfo?.quantity > 10 ? 'bg-green-100 text-green-800' : 
//                         basicInfo?.quantity > 0 ? 'bg-yellow-100 text-yellow-800' : 
//                         'bg-gray-100 text-gray-500'
//                       }`}>
//                         {basicInfo?.quantity > 0 
//                           ? `unité${basicInfo?.quantity > 1 ? 's' : ''} en stock` 
//                           : 'Rupture de stock'}
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Prix et actions */}
//                 <div className="space-y-6">
//                   {/* Prix */}
//                   <div>
//                     <span className="text-sm text-gray-500 block mb-1">Prix</span>
//                     <div className="flex items-baseline gap-3">
//                       <span className="text-4xl font-bold text-gray-900">
//                         {formatPrice(basicInfo?.price)} FCFA
//                       </span>
                      
//                       {basicInfo?.discount_price && (
//                         <div className="flex items-center gap-2">
//                           <span className="text-2xl text-gray-400 line-through">
//                             {formatPrice(basicInfo.discount_price)} FCFA
//                           </span>
//                           <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-sm font-bold rounded-lg border border-yellow-200">
//                             -{Math.round(((basicInfo.price - basicInfo.discount_price) / basicInfo.price) * 100)}%
//                           </span>
//                         </div>
//                       )}
//                     </div>
//                   </div>

//                   {/* Boutons d'action */}
//                   <div className="flex flex-col sm:flex-row gap-3">
//                     <button
//                       onClick={handleAddToCart}
//                       disabled={basicInfo?.quantity === 0 || addingToCart}
//                       className={`flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-bold transition-all ${getCartButtonClass()}`}
//                       title={
//                         basicInfo?.quantity === 0 ? "Produit en rupture de stock" :
//                         !isAuthenticated ? "Ajouter au panier local (non connecté)" :
//                         "Ajouter au panier"
//                       }
//                     >
//                       <FiShoppingCart className="text-xl" />
//                       {!isAuthenticated && <FiLogIn className="text-xs" />}
//                       <span className="text-lg">
//                         {getCartButtonText()}
//                       </span>
//                     </button>

//                     <button
//                       onClick={handleWhatsAppClick}
//                       className="flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-gray-500 to-gray-500 text-white rounded-xl font-bold hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
//                     >
//                       <FiMessageCircle className="text-xl" />
//                       <span className="text-lg">Discuter sur WhatsApp</span>
//                     </button>
//                   </div>

//                   {/* Informations supplémentaires */}
//                   <div className="pt-4 border-t border-gray-200">
//                     <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
//                       <div className="flex items-center gap-2 text-gray-600">
//                         <div className="w-2 h-2 bg-green-500 rounded-full"></div>
//                         <span>Livraison disponible</span>
//                       </div>
//                       <div className="flex items-center gap-2 text-gray-600">
//                         <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
//                         <span>Support 7j/7</span>
//                       </div>
//                       <div className="flex items-center gap-2 text-gray-600">
//                         <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
//                         <span>Paiement sécurisé</span>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* SECTION DES FOURNISSEURS - SEULEMENT POUR LES UTILISATEURS AUTORISÉS */}
//             {suppliers && suppliers.length > 0 && userCanViewSuppliers && (
//               <div className="p-6 sm:p-8 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
//                 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
//                   <div className="flex items-center gap-3">
//                     <div className={`p-2 ${primaryLight} rounded-lg`}>
//                       <FiTruck className={`text-xl ${primaryColor}`} />
//                     </div>
//                     <div>
//                       <h3 className="text-2xl font-bold text-gray-900">
//                         Informations fournisseurs
//                       </h3>
//                       <p className="text-sm text-gray-600">
//                         Accès: {getUserAccessLevel()} | {loadingSuppliers ? 'Chargement...' : `${suppliers.length} fournisseur(s)`}
//                       </p>
//                     </div>
//                   </div>
//                   <div className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-800 rounded-full border border-yellow-200">
//                     <FiShield className="text-sm" />
//                     <span className="text-sm font-semibold">Accès autorisé</span>
//                   </div>
//                 </div>
                
//                 <p className="text-gray-600 mb-8 max-w-3xl">
//                   Ce produit est disponible chez {suppliers.length} fournisseur{suppliers.length > 1 ? 's' : ''}. 
//                   {userCanContactSuppliers ? ' Vous pouvez contacter chacun d\'eux directement via WhatsApp.' : ''}
//                 </p>
                
//                 <div className="space-y-4">
//                   <h4 className="text-lg font-semibold text-gray-700">
//                     Liste des fournisseurs ({suppliers.length})
//                   </h4>
                  
//                   {loadingSuppliers ? (
//                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
//                       {[1, 2, 3, 4].map(i => (
//                         <div key={i} className="bg-white rounded-xl shadow-md border border-gray-200 p-4 animate-pulse">
//                           <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
//                           <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
//                           <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
//                           <div className="h-8 bg-gray-200 rounded"></div>
//                         </div>
//                       ))}
//                     </div>
//                   ) : (
//                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
//                       {suppliers.map((supplier, index) => (
//                         <div 
//                           key={supplier.id || index}
//                           className={`bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border overflow-hidden group ${
//                             userCanContactSuppliers ? 'cursor-pointer border-gray-200' : 'cursor-not-allowed border-gray-200'
//                           }`}
//                           onClick={() => userCanContactSuppliers && handleContactSupplier(supplier, basicInfo, images)}
//                         >
//                           <div className="p-4">
//                             {/* En-tête avec nom et ville */}
//                             <div className="mb-3">
//                               <h5 className="font-bold text-gray-800 group-hover:text-gray-900 transition-colors">
//                                 {supplier.name}
//                               </h5>
//                               {supplier.city && (
//                                 <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
//                                   <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
//                                     <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
//                                   </svg>
//                                   <span>{supplier.city}</span>
//                                 </div>
//                               )}
//                             </div>

//                             {/* Informations détaillées */}
//                             <div className="text-xs text-gray-500 space-y-1 mb-4">
//                               {supplier.email && (
//                                 <div className="truncate">📧 {supplier.email}</div>
//                               )}
//                               {supplier.address && (
//                                 <div className="truncate">📍 {supplier.address}</div>
//                               )}
//                               {supplier.phone && userCanContactSuppliers && (
//                                 <div className="truncate">📞 {supplier.phone}</div>
//                               )}
//                             </div>

//                             {/* Bouton WhatsApp */}
//                             {userCanContactSuppliers && (
//                               <div className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
//                                 supplier.phone 
//                                   ? 'bg-gray-100 text-gray-700 group-hover:bg-gray-800 group-hover:text-white' 
//                                   : 'bg-gray-100 text-gray-500 cursor-not-allowed'
//                               }`}>
//                                 <FiMessageSquare className="text-lg" />
//                                 <span className="font-semibold">
//                                   {!supplier.phone ? 'Sans contact' : 'Contacter sur WhatsApp'}
//                                 </span>
//                               </div>
//                             )}
//                           </div>
                          
//                           {/* Indicateur de clic */}
//                           {userCanContactSuppliers && (
//                             <div className={`text-xs font-medium px-3 py-1 text-center ${
//                               supplier.phone 
//                                 ? 'bg-gray-100 text-gray-700' 
//                                 : 'bg-gray-200 text-gray-500'
//                             }`}>
//                               {supplier.phone 
//                                 ? 'Cliquez pour contacter sur WhatsApp' 
//                                 : 'Contact non disponible'}
//                             </div>
//                           )}
//                         </div>
//                       ))}
//                     </div>
//                   )}
//                 </div>
//               </div>
//             )}

//             {/* Section informations supplémentaires (visible pour tous) */}
//             <div className="p-6 sm:p-8 border-t border-gray-200">
//               <div className="flex items-center gap-3 mb-4">
//                 <FiStar className="text-yellow-400 text-xl" />
//                 <h3 className="text-xl font-bold text-gray-900">Informations complémentaires</h3>
//               </div>
              
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                 <div className="space-y-3">
//                   <h4 className="font-semibold text-gray-700">Livraison</h4>
//                   <ul className="text-sm text-gray-600 space-y-2">
//                     <li className="flex items-center gap-2">
//                       <div className="w-2 h-2 bg-green-500 rounded-full"></div>
//                       <span>Suivi de commande disponible</span>
//                     </li>
//                     <li className="flex items-center gap-2">
//                       <div className="w-2 h-2 bg-green-500 rounded-full"></div>
//                       <span>Livraison dans tout le pays</span>
//                     </li>
//                   </ul>
//                 </div>
                
//                 <div className="space-y-3">
//                   <h4 className="font-semibold text-gray-700">Garantie & Support</h4>
//                   <ul className="text-sm text-gray-600 space-y-2">
//                     <li className="flex items-center gap-2">
//                       <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
//                       <span>Support client 7j/7 via WhatsApp</span>
//                     </li>
//                     <li className="flex items-center gap-2">
//                       <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
//                       <span>Service après-vente disponible</span>
//                     </li>
//                   </ul>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// export default ProductDetail;